import { ComponentCO2, PhasesCO2 } from '@acies/domain';
import { TranslateService } from '@ngx-translate/core';
import { numberToScientific } from './utils';

/** The PhasesCO2Model is responsible for viewing the phases of the currently selected ComponentCO2. */
export default class PhasesCO2Model {
	public epd: string;
	public name: string;
	public description: string;
	public a1: string;
	public a2: string;
	public a3: string;
	public a4: string;
	public a5: string;
	public c1: string;
	public c2: string;
	public c3: string;
	public c4: string;
	public d: string;
	public phasePattern: string;
	#currentComponentCO2: ComponentCO2 | null;
	#translateService: TranslateService;
	public constructor(translateService: TranslateService) {
		this.#currentComponentCO2 = null;
		this.#translateService = translateService;
		// RegExp that accepts an empty string or the scientific pattern 0[.,]00E+00
		this.phasePattern = '^([-]?[0-9][.,][0-9]{2}E[+-](0[0-9]|1[0-1]))?$';
		this.#resetPhasesUIValues();
		this.#translateService.onLangChange.subscribe(this.onLangChange.bind(this));
	}

	private onLangChange(): void {
		this.updatePhasesBindings(this.#currentComponentCO2);
	}

	public updatePhasesBindings(value: ComponentCO2 | null): void {
		this.#currentComponentCO2 = value;
		if (value) {
			const { epd, name, description, phases } = value;
			this.epd = epd;
			this.name = name;
			this.description = description;
			this.#setScientificValuesFromPhases(phases);
		} else {
			this.#resetPhasesUIValues();
		}
	}

	get #currentLang(): string {
		return this.#translateService.currentLang;
	}

	#resetPhasesUIValues(): void {
		this.epd = '';
		this.name = '';
		this.description = '';
		// store the default value once and reuse,
		const defaultScientific = this.#currentLang === 'en' ? '0.00E+00' : '0,00E+00';
		this.a1 = defaultScientific;
		this.a2 = defaultScientific;
		this.a3 = defaultScientific;
		this.a4 = defaultScientific;
		this.a5 = defaultScientific;
		this.c1 = defaultScientific;
		this.c2 = defaultScientific;
		this.c3 = defaultScientific;
		this.c4 = defaultScientific;
		this.d = defaultScientific;
	}

	#setScientificValuesFromPhases({ a1, a2, a3, a4, a5, c1, c2, c3, c4, d }: PhasesCO2): void {
		this.a1 = numberToScientific(a1, this.#currentLang);
		this.a2 = numberToScientific(a2, this.#currentLang);
		this.a3 = numberToScientific(a3, this.#currentLang);
		this.a4 = numberToScientific(a4, this.#currentLang);
		this.a5 = numberToScientific(a5, this.#currentLang);
		this.c1 = numberToScientific(c1, this.#currentLang);
		this.c2 = numberToScientific(c2, this.#currentLang);
		this.c3 = numberToScientific(c3, this.#currentLang);
		this.c4 = numberToScientific(c4, this.#currentLang);
		this.d = numberToScientific(d, this.#currentLang);
	}
}
